import { AuthenticatedHttpService } from '@/core/services/authenticated-http';
import { HttpService } from '@/core/services/http';
import { SuccessEnvelope } from '@/core/types/success-envelope';
import { HelpCenterFAQContent } from '@/help-center/types/help-center-faq-content';
import { HelpCenterVersionsContent } from '@/help-center/types/help-center-version-content';
import { LOAN_API_PREFIX } from '@/loan/constants/loan-api-prefix';
import { LOAN_CONTENTFUL_ACCESS_TOKEN } from '@/loan/constants/loan-contentful-access-token';
import { LOAN_CONTENTFUL_SPACE } from '@/loan/constants/loan-contentful-space';
import { Loan } from '@/loan/types/loan';
import { LoanCreateOneTimePaymentRequest } from '@/loan/types/loan-create-one-time-payment-request';
import { LoanCreateOneTimePaymentResponse } from '@/loan/types/loan-create-one-time-payment-response';
import { LoanCreateWithdrawalRequest } from '@/loan/types/loan-create-withdrawal-request';
import { LoanCreateWithdrawalResponse } from '@/loan/types/loan-create-withdrawal-response';
import { LoanDetails } from '@/loan/types/loan-details';
import { LoanLinkedAccount } from '@/loan/types/loan-linked-account';
import { LoanProjection } from '@/loan/types/loan-projection';
import { LoanProjectionRequestQuery } from '@/loan/types/loan-projection-request-query';
import { LoanRecurringPayment } from '@/loan/types/loan-recurring-payment';
import { LoanRecurringPaymentAvailableDates } from '@/loan/types/loan-recurring-payment-available-dates';
import { LoanStatement } from '@/loan/types/loan-statement';
import { LoanTransactionWithDetails } from '@/loan/types/loan-transaction-with-details';
import { LoanUpdateLoanRequest } from '@/loan/types/loan-update-loan-request';
import { UtilHelper } from '@/util/helper';

export class LoanAPIService {
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	private constructor() {}

	/**
	 * Creates a One-Time Payment
	 *
	 * @param loanId - string loan id
	 * @param request - LoanOneTimePaymentRequest
	 *
	 * @returns Promise<LoanTransactionWithDetails[]>
	 */
	public static createOneTimePayment(
		loanId: string,
		request: LoanCreateOneTimePaymentRequest,
	): Promise<LoanCreateOneTimePaymentResponse> {
		return AuthenticatedHttpService.post<SuccessEnvelope<Response>, LoanCreateOneTimePaymentRequest>(
			`${LOAN_API_PREFIX}/loan/${loanId}/one-time-payment`,
			request,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Creates a Withdrawal
	 *
	 * @param loanId - string loan id
	 * @param request - LoanCreateWithdrawalRequest
	 *
	 * @returns Promise<LoanCreateWithdrawalResponse>
	 */
	public static createWithdrawal(
		loanId: string,
		request: LoanCreateWithdrawalRequest,
	): Promise<SuccessEnvelope<LoanCreateWithdrawalResponse>> {
		return AuthenticatedHttpService.post<SuccessEnvelope<any>, LoanCreateWithdrawalRequest>(
			`${LOAN_API_PREFIX}/loan/${loanId}/withdrawal`,
			request,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static disableAutopay(
		loanId: string,
	): Promise<SuccessEnvelope<LoanCreateWithdrawalResponse>> {
		return AuthenticatedHttpService.patch<SuccessEnvelope<string>>(
			`${LOAN_API_PREFIX}/loan/${loanId}/disable-automatic-payments`,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Gets a loans Bank Accounts as LinkedAccounts
	 *
	 * @remarks this call uses the session JWT token
	 * to get the Linked Accounts vs passing the loanId
	 *
	 * @returns Promise<LoanLinkedAccount[]>
	 */
	public static getBankAccounts(): Promise<LoanLinkedAccount[]> {
		return AuthenticatedHttpService.get<SuccessEnvelope<LoanLinkedAccount[]>>(
			'/api/v1/bank-account?product=loan',
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Gets a loan's details
	 *
	 * @param loanId - string loan id
	 *
	 * @returns Promise<LoanDetails>
	 */
	public static getDetails(loanId: string): Promise<LoanDetails> {
		return AuthenticatedHttpService.get<SuccessEnvelope<LoanDetails>>(`${LOAN_API_PREFIX}/loan/${loanId}/details`)
			.then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Gets Help Center FAQ content from Contentful
	 *
	 * @returns Promise<HelpCenterFAQContent>
	 */
	public static getHelpCenterFAQContent(): Promise<HelpCenterFAQContent> {
		// Using the sdk client doesn't work with the loan Contentful space,
		// so the url is statically set here for now
		// This has been brought up with Justin S. of Product - JR 02/24/2021
		return HttpService.get<HelpCenterFAQContent>(
			`https://cdn.contentful.com/spaces
			/${LOAN_CONTENTFUL_SPACE}
			/entries?access_token=${LOAN_CONTENTFUL_ACCESS_TOKEN}&content_type=topicList&include=2`,
		);
	}

	/**
	 * Gets Help Center Version content from Contentful
	 *
	 * @returns Promise<HelpCenterVersionsContent>
	 */
	public static getHelpCenterVersionsContent(): Promise<HelpCenterVersionsContent> {
		// Using the sdk client doesn't work with the loan Contentful space,
		// so the url is statically set here for now
		// This has been brought up with Justin S. of Product - JR 03/03/2021
		return HttpService.get<HelpCenterVersionsContent>(
			`https://cdn.contentful.com/spaces
			/${LOAN_CONTENTFUL_SPACE}
			/entries?access_token=${LOAN_CONTENTFUL_ACCESS_TOKEN}&content_type=releaseNote&order=-fields.date`,
		);
	}

	/**
	 * Gets a loan's Linked Accounts
	 *
	 * @remarks this call uses the session JWT token
	 * to get the Linked Accounts vs passing the loanId
	 *
	 * @returns Promise<LoanLinkedAccount[]>
	 */
	public static getLinkedAccounts(): Promise<LoanLinkedAccount[]> {
		return AuthenticatedHttpService.get<SuccessEnvelope<LoanLinkedAccount[]>>(
			'/api/kloans-iav/linked-accounts',
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Gets array of loans
	 *
	 * @returns Promise<Loan[]>
	 */
	public static getLoans(): Promise<Loan[]> {
		return AuthenticatedHttpService.get<SuccessEnvelope<Loan[]>>(`${LOAN_API_PREFIX}/loan`)
			.then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Gets a loan's details
	 *
	 * @param loanId - string loan id
	 *
	 * @returns Promise<LoanDetails>
	 */
	public static getProjection(loanId: string, query?: LoanProjectionRequestQuery): Promise<LoanProjection> {
		const base = `/api/v3/loan/${loanId}/projection`;
		const url = query ? `${base}?${query.property}=${query.value}` : base;

		return AuthenticatedHttpService.get<SuccessEnvelope<LoanProjection>>(url)
			.then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Gets a loan's Recurring Payment Available Dates
	 *
	 * @param id - string loan id
	 *
	 * @returns Promise<LoanRecurringPayment>
	 */
	public static getRecurringPaymentAvailableDates(
		loanId: string,
	): Promise<LoanRecurringPaymentAvailableDates> {
		return AuthenticatedHttpService.get<SuccessEnvelope<LoanRecurringPaymentAvailableDates>>(
			`${LOAN_API_PREFIX}/v2/loan/${loanId}/recurring-payment/available-dates`,
		).then((response) => response.data);
	}

	/**
	 * Gets a loan's Recurring Payment
	 *
	 * @param id - string loan id
	 *
	 * @returns Promise<LoanRecurringPayment>
	 */
	public static getRecurringPayment(
		loanId: string,
	): Promise<LoanRecurringPayment | null> {
		return AuthenticatedHttpService.get<SuccessEnvelope<LoanRecurringPayment | null>>(
			`${LOAN_API_PREFIX}/v2/loan/${loanId}/recurring-payment`,
		).then((response) => response.data);
	}

	/**
	 * Gets a loan's Statements
	 *
	 * @param loanId - string loan id
	 *
	 * @returns Promise<LoanStatement[]>
	 */
	public static getStatements(loanId: string): Promise<LoanStatement[]> {
		return AuthenticatedHttpService.get<SuccessEnvelope<LoanStatement[]>>(`${LOAN_API_PREFIX}/loan/${loanId}/statements/available`)
			.then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	/**
	 * Gets a loan's Transactions with Details
	 *
	 * @param id - string loan id
	 *
	 * @returns Promise<LoanTransactionWithDetails[]>
	 */
	public static getTransactionsWithDetails(
		loanId: string,
	): Promise<LoanTransactionWithDetails[]> {
		return AuthenticatedHttpService.get<SuccessEnvelope<LoanTransactionWithDetails[]>>(
			`${LOAN_API_PREFIX}/loan/${loanId}/transactions?expand=loan_transaction_details`,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}

	public static setRecurringPayment(
		loanId: string,
		request: LoanRecurringPayment,
	): Promise<LoanRecurringPayment> {
		return AuthenticatedHttpService.post<SuccessEnvelope<LoanRecurringPayment>, LoanRecurringPayment>(
			`${LOAN_API_PREFIX}/v2/loan/${loanId}/recurring-payment`,
			request,
		).then((response) => response.data);
	}

	/**
	 * Updates a Loan
	 *
	 * @returns Promise<string>
	 */
	public static updateLoan(
		loanId: string,
		request: LoanUpdateLoanRequest,
	): Promise<string> {
		return AuthenticatedHttpService.patch<SuccessEnvelope<string>, LoanUpdateLoanRequest>(
			`${LOAN_API_PREFIX}/loan/${loanId}`,
			request,
		).then((response) => UtilHelper.snakeObjectToCamel(response.data));
	}
}
